import React, { useEffect, useMemo, useCallback, useState } from "react";
import { HiOutlineX } from "react-icons/hi";
import { listOfSubscriptions } from "../../../../common/envConfig";
import moment from "moment";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Select from "../../../../components/shared/Select/Select";
import Input from "../../../../components/shared/Input/Input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { twMerge } from "tailwind-merge";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { SubscriptionFilter } from "./SubscriptionFilter";

// Validation schema for form fields
const formSchema = z.object({
  createdAtFrom: z.string().optional(),
  createdAtTo: z.string().optional(),
  studentCreditsMin: z.string().optional(),
  studentCreditsMax: z.string().optional(),
  reportCreditsMin: z.string().optional(),
  reportCreditsMax: z.string().optional(),
});

const genderOptions = [
  { text: "Male", value: "Male" },
  { text: "Female", value: "Female" },
  { text: "Prefer Not to Answer", value: "prefer not to answer" },
];

const dominantHandOptions = [
  { text: "Left", value: "Left" },
  { text: "Right", value: "Right" },
];

const secondarySubTypeOptions = [
  { text: "None", value: "none" },
  { text: "Free", value: listOfSubscriptions.STUDENT_FREE },
  { text: "3D Player", value: listOfSubscriptions.STUDENT_LITE_ANNUALLY },
  { text: "3D Pro", value: listOfSubscriptions.PREMIUM_ANNUALLY },
];

export function UserFiltersPanel({ isOpen, onClose, onSubmit }) {
  const { usersState, setUsersFilters } = useAdminSearchStore();
  const { filters } = usersState;

  const [draftUserInput, setDraftUserInput] = useState(filters.userInput ?? "");
  const [draftSubscriptions, setDraftSubscriptions] = useState([
    ...(filters.subscriptionType ?? []),
  ]);
  const [draftFilters, setDraftFilters] = useState({
    ...filters,
    userInput: undefined,
    subscriptionType: undefined,
  });

  useEffect(() => {
    if (isOpen) {
      setDraftUserInput(filters.userInput ?? "");
      setDraftSubscriptions([...(filters.subscriptionType ?? [])]);
      setDraftFilters({
        ...filters,
        userInput: undefined,
        subscriptionType: undefined,
      });
    }
  }, [isOpen, filters]);

  // Initialize form with values from store
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      createdAtFrom: draftFilters.userCreated?.from
        ? moment(draftFilters.userCreated.from).format("YYYY-MM-DD")
        : "",
      createdAtTo: draftFilters.userCreated?.to
        ? moment(draftFilters.userCreated.to).format("YYYY-MM-DD")
        : "",
      studentCreditsMin: draftFilters.studentCredits?.min?.toString() || "",
      studentCreditsMax: draftFilters.studentCredits?.max?.toString() || "",
      reportCreditsMin: draftFilters.reportCredits?.min?.toString() || "",
      reportCreditsMax: draftFilters.reportCredits?.max?.toString() || "",
    },
    mode: "onChange",
  });

  const hasActiveFilters = useMemo(() => {
    if (draftUserInput?.trim()) return true;
    if (draftSubscriptions.length > 0) return true;
    if (draftFilters.gender) return true;
    if (draftFilters.dominantHand) return true;
    if (draftFilters.secondarySubType) return true;
    if (draftFilters.userCreated?.from || draftFilters.userCreated?.to)
      return true;
    if (
      draftFilters.studentCredits?.min != null ||
      draftFilters.studentCredits?.max != null
    )
      return true;
    if (
      draftFilters.reportCredits?.min != null ||
      draftFilters.reportCredits?.max != null
    )
      return true;
    return false;
  }, [draftUserInput, draftSubscriptions, draftFilters]);

  const handleClear = useCallback(() => {
    // Reset local draft state
    setDraftUserInput("");
    setDraftSubscriptions([]);
    setDraftFilters({
      gender: null,
      dominantHand: null,
      userCreated: { from: null, to: null },
      secondarySubType: null,
      studentCredits: { min: null, max: null },
      reportCredits: { min: null, max: null },
    });
    setValue("createdAtFrom", "");
    setValue("createdAtTo", "");
    setValue("studentCreditsMin", "");
    setValue("studentCreditsMax", "");
    setValue("reportCreditsMin", "");
    setValue("reportCreditsMax", "");
    setUsersFilters({
      filters: {
        userInput: "",
        subscriptionType: [],
        gender: null,
        dominantHand: null,
        userCreated: { from: null, to: null },
        secondarySubType: null,
        studentCredits: { min: null, max: null },
        reportCredits: { min: null, max: null },
      },
    });
    onSubmit?.();
  }, [
    setDraftUserInput,
    setDraftSubscriptions,
    setDraftFilters,
    setValue,
    setUsersFilters,
    onSubmit,
  ]);

  const handleFormSubmit = useCallback(
    (data) => {
      const userCreated = {
        from: data.createdAtFrom
          ? moment(data.createdAtFrom).startOf("day").valueOf()
          : null,
        to: data.createdAtTo
          ? moment(data.createdAtTo).endOf("day").valueOf()
          : null,
      };
      const studentCredits = {
        min: data.studentCreditsMin ? Number(data.studentCreditsMin) : null,
        max: data.studentCreditsMax ? Number(data.studentCreditsMax) : null,
      };
      const reportCredits = {
        min: data.reportCreditsMin ? Number(data.reportCreditsMin) : null,
        max: data.reportCreditsMax ? Number(data.reportCreditsMax) : null,
      };

      setUsersFilters({
        filters: {
          ...draftFilters,
          userInput: draftUserInput ?? "",
          subscriptionType: draftSubscriptions ?? [],
          userCreated,
          studentCredits,
          reportCredits,
        },
      });
      onSubmit?.();
    },
    [
      draftUserInput,
      draftSubscriptions,
      draftFilters,
      setUsersFilters,
      onSubmit,
    ]
  );

  if (!isOpen) return null;

  return (
    <div
      aria-label="Filters"
      aria-modal="true"
      className="font-manrope fixed right-0 top-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l border-gray200 bg-white p-6"
      role="dialog"
    >
      {/* Header */}
      <div className="relative flex flex-col gap-1">
        <div className="text-2xl font-semibold">Filters</div>

        <div className="text-base text-gray600">
          Filter users by various criteria
        </div>

        <button
          aria-label="Close"
          className="absolute right-0 top-0 w-fit rounded-sm p-2"
          onClick={onClose}
        >
          <HiOutlineX className="size-6 text-gray500" />
        </button>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto scrollbar-hide">
        {/* Search */}
        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Search</label>
          <input
            className="h-12 w-full rounded-lg border border-gray300 px-4 text-base outline-none transition-all duration-300 placeholder:text-gray500 focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
            placeholder="Search by username or email…"
            type="text"
            value={draftUserInput || ""}
            onChange={(e) => setDraftUserInput(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-base text-gray700">Subscriptions</div>
          <SubscriptionFilter
            draftSubscriptions={draftSubscriptions}
            setDraftSubscriptions={setDraftSubscriptions}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Gender</label>
          <Select
            options={genderOptions}
            value={draftFilters.gender || null}
            placeholder="Any gender"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                gender: val === draftFilters.gender ? null : val,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Dominant Hand</label>
          <Select
            options={dominantHandOptions}
            value={draftFilters.dominantHand || null}
            placeholder="Any hand"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                dominantHand: val === draftFilters.dominantHand ? null : val,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">
            Account Creation Date
          </label>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray600">From</label>
              <Input
                control={control}
                name="createdAtFrom"
                type="date"
                size="md"
                fullWidth={true}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray600">To</label>
              <Input
                control={control}
                name="createdAtTo"
                type="date"
                size="md"
                fullWidth={true}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Credits</label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                control={control}
                name="studentCreditsMin"
                placeholder="Min"
                type="number"
                inputMode="numeric"
                size="md"
                fullWidth={true}
                className="[&>div>input]:min-w-0"
              />
            </div>
            <span className="text-base text-gray600 font-semibold">-</span>
            <div className="flex-1">
              <Input
                control={control}
                name="studentCreditsMax"
                placeholder="Max"
                type="number"
                inputMode="numeric"
                size="md"
                fullWidth={true}
                className="[&>div>input]:min-w-0"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Report Credits</label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                control={control}
                name="reportCreditsMin"
                placeholder="Min"
                type="number"
                inputMode="numeric"
                size="md"
                fullWidth={true}
                className="[&>div>input]:min-w-0"
              />
            </div>
            <span className="text-base text-gray600 font-semibold">-</span>
            <div className="flex-1">
              <Input
                control={control}
                name="reportCreditsMax"
                placeholder="Max"
                type="number"
                inputMode="numeric"
                size="md"
                fullWidth={true}
                className="[&>div>input]:min-w-0"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Secondary Sub Type</label>
          <Select
            options={secondarySubTypeOptions}
            value={draftFilters.secondarySubType || null}
            placeholder="Any type"
            withCheckbox
            buttonProps={{ size: "md" }}
            onChange={(val) =>
              setDraftFilters((prev) => ({
                ...prev,
                secondarySubType:
                  val === draftFilters.secondarySubType ? null : val,
              }))
            }
          />
        </div>
      </div>

      {/* Footer */}

      <div className="mt-auto">
        <Divider type="horizontal" color="gray200" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={twMerge(
              "text-lg font-semibold",
              !hasActiveFilters ? "text-gray300" : "text-primary700"
            )}
            onClick={handleClear}
          >
            Clear all
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              isDisabled={!isDirty && !hasActiveFilters}
              onClick={handleSubmit(handleFormSubmit)}
              size="md"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
